import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClient } from "../api";
import { isResponseSuccess } from "../utils";
import { URLS } from "../constants";
import { IClientFloors } from "../all-types";


export const getFloorList = createAsyncThunk(
  "floors/getFloorList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(URLS.GET_FLOORS);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to fetch floors");
      }
      return response.data.data as IClientFloors[];
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not fetch floors");
    }
  }
);

/**
 * Async Thunk to create a new floor.
 * @param details - An object containing the floor data to be created.
 */
export const createFloor = createAsyncThunk(
  "floors/createFloor",
  async (details: { floor: Omit<IClientFloors, '_id'> }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post(URLS.ADD_FLOOR, {floor: details.floor});
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to add floor");
      }
      // Optionally re-fetch the list after creation to update the UI
      dispatch(getFloorList());
      return response.data.data as IClientFloors;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not add new floor");
    }
  }
);

/**
 * Async Thunk to update an existing floor.
 * @param details - An object containing the floor ID and the updated floor data.
 */
export const updateFloor = createAsyncThunk(
  "floors/updateFloor",
  async (details: { id: string; floor: Omit<IClientFloors, '_id'>  }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put(URLS.UPDATE_FLOOR(details.id), details.floor);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update floor");
      }
      // Optionally re-fetch the list after update to refresh the UI
      dispatch(getFloorList());
      return response.data.data as IClientFloors;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update floor");
    }
  }
);

/**
 * Async Thunk to delete a floor.
 * @param id - The ID of the floor to be deleted.
 */
export const deleteFloor = createAsyncThunk(
  "floors/deleteFloor",
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.delete(URLS.DELETE_FLOOR(id));
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to delete floor");
      }
      // Optionally re-fetch the list after deletion to update the UI
      dispatch(getFloorList());
      return id; // Return the ID of the deleted item on success
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not delete floor");
    }
  }
);